import { useEditor, EditorContent, BubbleMenu } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import Youtube from "@tiptap/extension-youtube";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import { Markdown } from "tiptap-markdown";
import { useCallback, useEffect, useState } from "react";
import { EditorToolbar } from "./EditorToolbar";
import { ImageUploadDialog } from "./ImageUploadDialog";

/**
 * Extension set shared by the editor and by anything that needs an equivalent
 * Tiptap instance (EditorToolbar story, future previews). Keep it here so the
 * toolbar and the editor never drift.
 */
export function buildExtensions(placeholder = "Comece a escrever...") {
  return [
    StarterKit.configure({
      heading: {
        levels: [1, 2, 3],
      },
    }),

    Image.configure({
      HTMLAttributes: {
        class: "rounded-lg max-w-full",
      },
    }),

    Link.configure({
      openOnClick: false,

      HTMLAttributes: {
        class: "text-primary underline hover:no-underline",
      },
    }),

    Placeholder.configure({
      placeholder,
    }),

    Underline,

    Youtube.configure({
      HTMLAttributes: {
        class: "w-full aspect-video rounded-lg",
      },

      width: 640,

      height: 360,
    }),

    Table.configure({
      resizable: true,

      HTMLAttributes: {
        class: "border-collapse w-full",
      },
    }),

    TableRow,

    TableCell.configure({
      HTMLAttributes: {
        class: "border border-border p-2",
      },
    }),

    TableHeader.configure({
      HTMLAttributes: {
        class: "border border-border p-2 bg-muted font-semibold",
      },
    }),

    Markdown.configure({
      html: true,

      transformPastedText: true,

      transformCopiedText: true,
    }),
  ];
}

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  /** id do rótulo do campo; vai para o contenteditable (role=textbox). */
  "aria-labelledby"?: string;
}

export function RichTextEditor({
  content,
  onChange,
  placeholder = "Comece a escrever...",
  "aria-labelledby": labelledBy,
}: RichTextEditorProps) {
  const [imageDialogOpen, setImageDialogOpen] = useState(false);

  const editor = useEditor({
    extensions: buildExtensions(placeholder),
    content: "",
    editorProps: {
      attributes: {
        class: "prose prose-lg dark:prose-invert max-w-none min-h-[400px] p-4 focus:outline-none",
        ...(labelledBy ? { "aria-labelledby": labelledBy } : {}),
      },
    },
    onUpdate: ({ editor }) => {
      const markdown = editor.storage.markdown.getMarkdown();
      onChange(markdown);
    },
  });

  // Load initial content
  useEffect(() => {
    if (editor && content && !editor.isDestroyed) {
      const currentContent = editor.storage.markdown.getMarkdown();
      if (currentContent !== content) {
        editor.commands.setContent(content);
      }
    }
  }, [editor, content]);

  const addImage = useCallback(
    (url: string) => {
      if (editor && url) {
        editor.chain().focus().setImage({ src: url }).run();
      }
      setImageDialogOpen(false);
    },
    [editor],
  );

  const addYoutubeVideo = useCallback(() => {
    const url = window.prompt("Cole a URL do vídeo do YouTube:");
    if (url && editor) {
      editor.commands.setYoutubeVideo({ src: url });
    }
  }, [editor]);

  const addLink = useCallback(() => {
    const previousUrl = editor?.getAttributes("link").href;
    const url = window.prompt("URL do link:", previousUrl);

    if (url === null) return;

    if (url === "") {
      editor?.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor?.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const insertTable = useCallback(() => {
    editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  if (!editor) {
    return <div className="min-h-[400px] animate-pulse rounded-lg border bg-muted/50" />;
  }

  return (
    <div className="overflow-hidden rounded-lg border bg-background">
      <EditorToolbar
        editor={editor}
        onImageClick={() => setImageDialogOpen(true)}
        onYoutubeClick={addYoutubeVideo}
        onLinkClick={addLink}
        onTableClick={insertTable}
      />

      {editor && (
        <BubbleMenu
          editor={editor}
          tippyOptions={{ duration: 100 }}
          className="flex items-center gap-1 rounded-lg border border-border bg-popover p-1 shadow-lg"
        >
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            aria-label="Negrito"
            className={`rounded p-1.5 hover:bg-muted ${editor.isActive("bold") ? "bg-muted text-primary" : ""}`}
          >
            <span className="text-sm font-bold">B</span>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            aria-label="Itálico"
            className={`rounded p-1.5 hover:bg-muted ${editor.isActive("italic") ? "bg-muted text-primary" : ""}`}
          >
            <span className="text-sm italic">I</span>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            aria-label="Sublinhado"
            className={`rounded p-1.5 hover:bg-muted ${editor.isActive("underline") ? "bg-muted text-primary" : ""}`}
          >
            <span className="text-sm underline">U</span>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            aria-label="Tachado"
            className={`rounded p-1.5 hover:bg-muted ${editor.isActive("strike") ? "bg-muted text-primary" : ""}`}
          >
            <span className="text-sm line-through">S</span>
          </button>
          <div className="mx-1 h-4 w-px bg-border" />
          <button
            type="button"
            onClick={addLink}
            aria-label="Inserir link"
            className={`rounded p-1.5 text-sm hover:bg-muted ${editor.isActive("link") ? "bg-muted text-primary" : ""}`}
          >
            🔗
          </button>
        </BubbleMenu>
      )}

      <EditorContent editor={editor} />

      <ImageUploadDialog
        open={imageDialogOpen}
        onOpenChange={setImageDialogOpen}
        onImageSelect={addImage}
      />
    </div>
  );
}
